import { useEffect, useState, useMemo } from "react";

/**
 * HeroBackground — CMS-controlled backdrop for the hero visual card.
 *
 *   hero.background_mode = "gradient" | "image" | "slideshow" | "animated"
 *   hero.background_image_url  → used when mode = "image"
 *   hero.slideshow_images = [{ url, alt }]  → used when mode = "slideshow"
 *   hero.slideshow_interval_ms = 4500       → per-slide duration
 *   hero.animation_preset = "aurora" | "grid" | "highway" | "waves" | "sparks" | "orbit"
 */

const ANIMATION_PRESETS = ["aurora", "grid", "highway", "waves", "sparks", "orbit"];

export default function HeroBackground({ hero }) {
  const mode = hero?.background_mode || (hero?.background_image_url ? "image" : "gradient");
  const preset = ANIMATION_PRESETS.includes(hero?.animation_preset) ? hero.animation_preset : "aurora";

  const slides = useMemo(
    () => (hero?.slideshow_images || []).filter((s) => s && s.url),
    [hero?.slideshow_images],
  );
  const interval = Math.max(1500, Number(hero?.slideshow_interval_ms) || 4500);

  const [slideIdx, setSlideIdx] = useState(0);
  useEffect(() => {
    if (mode !== "slideshow" || slides.length < 2) return;
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % slides.length), interval);
    return () => clearInterval(t);
  }, [mode, slides.length, interval]);

  return (
    <>
      <HeroBackgroundStyles />
      <div className="absolute inset-0 h-full w-full overflow-hidden">
        {/* Always-dark base so animations always pop, regardless of theme */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at top left, color-mix(in srgb, var(--pub-primary) 50%, #0B1220) 0%, #0B1220 55%, #060912 100%)",
          }}
          aria-hidden
        />

        {mode === "image" && hero?.background_image_url && (
          <img
            src={hero.background_image_url}
            alt="Fuel bowser delivery"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}

        {mode === "video" && hero?.background_video_url && (
          <video
            src={hero.background_video_url}
            className="absolute inset-0 h-full w-full object-contain"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden
          />
        )}

        {mode === "slideshow" && slides.length > 0 && (
          <div className="absolute inset-0">
            {slides.map((s, i) => (
              <img
                key={s.url}
                src={s.url}
                alt={s.alt || `Slide ${i + 1}`}
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ${i === slideIdx ? "opacity-100" : "opacity-0"}`}
                loading={i === 0 ? "eager" : "lazy"}
              />
            ))}
            {slides.length > 1 && (
              <div
                className="absolute bottom-24 left-1/2 z-10 flex -translate-x-1/2 gap-1.5"
                data-testid="hero-slideshow-dots"
              >
                {slides.map((s, i) => (
                  <button
                    key={s.url}
                    onClick={() => setSlideIdx(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all ${i === slideIdx ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {mode === "animated" && <AnimatedLayer preset={preset} />}

        {/* Vignette overlay for legibility of the floating snapshot card — kept light so animations pop */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" aria-hidden />
      </div>
    </>
  );
}

/* ─────────── Animation presets ─────────── */

function AnimatedLayer({ preset }) {
  if (preset === "aurora") return <Aurora />;
  if (preset === "grid") return <GridPulse />;
  if (preset === "highway") return <Highway />;
  if (preset === "waves") return <Waves />;
  if (preset === "sparks") return <Sparks />;
  if (preset === "orbit") return <Orbit />;
  return <Aurora />;
}

const Aurora = () => (
  <div className="absolute inset-0" aria-hidden data-testid="hero-anim-aurora">
    <div className="aurora-blob aurora-blob-1" />
    <div className="aurora-blob aurora-blob-2" />
    <div className="aurora-blob aurora-blob-3" />
    <div className="aurora-noise" />
  </div>
);

const GridPulse = () => (
  <div className="absolute inset-0" aria-hidden data-testid="hero-anim-grid">
    <div className="grid-pattern" />
    <div className="grid-scanline" />
  </div>
);

const Highway = () => (
  <div className="absolute inset-0" aria-hidden data-testid="hero-anim-highway">
    <div className="highway-road" />
    <div className="highway-lane highway-lane-1" />
    <div className="highway-lane highway-lane-2" />
    <div className="highway-glow" />
  </div>
);

const Waves = () => (
  <div className="absolute inset-0" aria-hidden data-testid="hero-anim-waves">
    <svg className="wave-svg" viewBox="0 0 1200 400" preserveAspectRatio="none">
      <path className="wave-path wave-path-1" d="M0,200 C300,120 600,280 900,180 C1050,130 1150,200 1200,180 L1200,400 L0,400 Z" />
      <path className="wave-path wave-path-2" d="M0,240 C300,320 600,180 900,260 C1050,300 1150,220 1200,240 L1200,400 L0,400 Z" />
      <path className="wave-path wave-path-3" d="M0,280 C300,220 600,340 900,280 C1050,240 1150,300 1200,290 L1200,400 L0,400 Z" />
    </svg>
  </div>
);

const Sparks = () => {
  const particles = useMemo(
    () => Array.from({ length: 24 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 8,
      duration: 6 + Math.random() * 6,
      size: 3 + Math.random() * 5,
    })),
    [],
  );
  return (
    <div className="absolute inset-0" aria-hidden data-testid="hero-anim-sparks">
      {particles.map((p) => (
        <span
          key={p.id}
          className="spark"
          style={{
            left: `${p.left}%`,
            width: `${p.size}px`,
            height: `${p.size}px`,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
          }}
        />
      ))}
    </div>
  );
};

const Orbit = () => (
  <div className="absolute inset-0 flex items-center justify-center" aria-hidden data-testid="hero-anim-orbit">
    <div className="orbit-ring orbit-ring-1" />
    <div className="orbit-ring orbit-ring-2" />
    <div className="orbit-ring orbit-ring-3" />
    <div className="orbit-core" />
  </div>
);

/* ─────────── Styles (scoped via unique class names) ─────────── */

function HeroBackgroundStyles() {
  return (
    <style>{`
      /* Aurora ------------------------------------------- */
      .aurora-blob{position:absolute;border-radius:50%;filter:blur(70px);opacity:.85;mix-blend-mode:screen;}
      .aurora-blob-1{width:65%;height:65%;background:var(--pub-primary);top:-8%;left:-8%;animation:aurora-move-1 14s ease-in-out infinite alternate;}
      .aurora-blob-2{width:70%;height:70%;background:color-mix(in srgb, var(--pub-primary) 40%, #ff6ec4);bottom:-12%;right:-12%;animation:aurora-move-2 18s ease-in-out infinite alternate;}
      .aurora-blob-3{width:55%;height:55%;background:color-mix(in srgb, var(--pub-primary) 70%, #7afcff);top:30%;left:30%;opacity:.7;animation:aurora-move-3 16s ease-in-out infinite alternate;}
      .aurora-noise{position:absolute;inset:0;background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.35 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");opacity:.18;mix-blend-mode:overlay;}
      @keyframes aurora-move-1{0%{transform:translate(0,0) scale(1)}100%{transform:translate(40%,25%) scale(1.25)}}
      @keyframes aurora-move-2{0%{transform:translate(0,0) scale(1)}100%{transform:translate(-30%,-20%) scale(1.3)}}
      @keyframes aurora-move-3{0%{transform:translate(0,0) scale(.85)}100%{transform:translate(-25%,30%) scale(1.2)}}

      /* Grid pulse ---------------------------------------- */
      .grid-pattern{position:absolute;inset:0;background-image:linear-gradient(color-mix(in srgb, var(--pub-primary) 55%, white) 1px,transparent 1px),linear-gradient(90deg,color-mix(in srgb, var(--pub-primary) 55%, white) 1px,transparent 1px);background-size:44px 44px;mask-image:radial-gradient(ellipse at center,black 20%,transparent 80%);opacity:.45;animation:grid-pulse 4s ease-in-out infinite;}
      .grid-scanline{position:absolute;left:0;right:0;height:30%;top:-30%;background:linear-gradient(180deg,transparent,color-mix(in srgb, var(--pub-primary) 60%, transparent),transparent);animation:grid-scan 4.5s linear infinite;}
      @keyframes grid-pulse{0%,100%{opacity:.35}50%{opacity:.7}}
      @keyframes grid-scan{0%{top:-30%}100%{top:100%}}

      /* Highway ------------------------------------------- */
      .highway-road{position:absolute;left:0;right:0;bottom:0;height:70%;background:linear-gradient(180deg,transparent,rgba(0,0,0,.6));transform:perspective(600px) rotateX(55deg);transform-origin:bottom;}
      .highway-lane{position:absolute;left:50%;bottom:0;width:8px;height:200%;background-image:repeating-linear-gradient(0deg,var(--pub-primary) 0,var(--pub-primary) 26px,transparent 26px,transparent 42px);transform:translateX(-50%);animation:highway-move 1.1s linear infinite;transform-origin:bottom;filter:drop-shadow(0 0 8px var(--pub-primary));}
      .highway-lane-1{transform:translateX(-50%) perspective(600px) rotateX(55deg);}
      .highway-lane-2{transform:translateX(-50%) perspective(600px) rotateX(55deg);animation-delay:.55s;opacity:.55;}
      .highway-glow{position:absolute;inset:0;background:radial-gradient(ellipse at 50% 90%,color-mix(in srgb, var(--pub-primary) 60%, transparent),transparent 60%);pointer-events:none;}
      @keyframes highway-move{0%{background-position-y:0}100%{background-position-y:42px}}

      /* Waves --------------------------------------------- */
      .wave-svg{position:absolute;inset:0;width:100%;height:100%;}
      .wave-path{fill:var(--pub-primary);opacity:.35;}
      .wave-path-1{animation:wave-drift 9s ease-in-out infinite alternate;}
      .wave-path-2{animation:wave-drift 12s ease-in-out infinite alternate-reverse;opacity:.5;fill:color-mix(in srgb, var(--pub-primary) 45%, white);}
      .wave-path-3{animation:wave-drift 15s ease-in-out infinite alternate;opacity:.65;fill:var(--pub-primary);}
      @keyframes wave-drift{0%{transform:translateX(-6%) translateY(0)}100%{transform:translateX(6%) translateY(-8px)}}

      /* Sparks -------------------------------------------- */
      .spark{position:absolute;bottom:-10px;border-radius:50%;background:radial-gradient(circle,color-mix(in srgb, var(--pub-primary) 40%, white) 0%,var(--pub-primary) 45%,transparent 100%);box-shadow:0 0 16px var(--pub-primary),0 0 32px color-mix(in srgb, var(--pub-primary) 70%, transparent);animation:spark-rise linear infinite;opacity:.95;}
      @keyframes spark-rise{0%{transform:translateY(0) scale(.6);opacity:0}10%{opacity:.95}90%{opacity:.95}100%{transform:translateY(-120vh) scale(.3);opacity:0}}

      /* Orbit --------------------------------------------- */
      .orbit-ring{position:absolute;border:2px solid color-mix(in srgb, var(--pub-primary) 65%, transparent);border-radius:50%;top:50%;left:50%;translate:-50% -50%;box-shadow:0 0 20px color-mix(in srgb, var(--pub-primary) 30%, transparent) inset;}
      .orbit-ring-1{width:220px;height:220px;animation:orbit-spin 12s linear infinite;}
      .orbit-ring-2{width:340px;height:340px;border-color:color-mix(in srgb, var(--pub-primary) 45%, transparent);animation:orbit-spin 18s linear infinite reverse;}
      .orbit-ring-3{width:460px;height:460px;border-color:color-mix(in srgb, var(--pub-primary) 25%, transparent);animation:orbit-spin 26s linear infinite;}
      .orbit-ring::before{content:"";position:absolute;top:-8px;left:50%;width:16px;height:16px;background:var(--pub-primary);border-radius:50%;box-shadow:0 0 24px var(--pub-primary),0 0 48px var(--pub-primary);translate:-50% 0;}
      .orbit-core{width:100px;height:100px;background:radial-gradient(circle,color-mix(in srgb, var(--pub-primary) 30%, white),var(--pub-primary) 70%);border-radius:50%;box-shadow:0 0 80px var(--pub-primary),0 0 40px var(--pub-primary) inset;animation:orbit-core-pulse 3s ease-in-out infinite;}
      @keyframes orbit-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
      @keyframes orbit-core-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}

      /* Reduced-motion — respect user preferences */
      @media (prefers-reduced-motion: reduce){
        .aurora-blob,.grid-pattern,.grid-scanline,.highway-lane,.wave-path,.spark,.orbit-ring{animation:none !important;}
      }
    `}</style>
  );
}
